import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  ListGHAEmailDomainRulesFilters,
  UpsertGHAEmailDomainRuleBody,
  bulkUpsertGHAEmailDomainRules,
  deleteGHAEmailDomainRule,
  listGHAEmailDomainRules,
  upsertGHAEmailDomainRule,
} from '~/services/ConnectionGoogleService';

export const ghaEmailDomainRuleQueryKeys = {
  all: ['ghaEmailDomainRules'] as const,
  lists: () => [...ghaEmailDomainRuleQueryKeys.all, 'list'] as const,
  list: (filters: ListGHAEmailDomainRulesFilters) => [...ghaEmailDomainRuleQueryKeys.lists(), filters] as const,
};

export function useGHAEmailDomainRules(filters: ListGHAEmailDomainRulesFilters = {}) {
  return useQuery({
    queryKey: ghaEmailDomainRuleQueryKeys.list(filters),
    queryFn: async ({ signal }) => {
      const response = await listGHAEmailDomainRules(filters, { signal });
      return { result: response.result, total: response.total };
    },
    // Keep the current rows visible while a filter or page change refetches.
    placeholderData: keepPreviousData,
  });
}

export function useUpsertGHAEmailDomainRule() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ domain, input }: { domain: string; input: UpsertGHAEmailDomainRuleBody }) =>
      await upsertGHAEmailDomainRule(domain, input),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ghaEmailDomainRuleQueryKeys.lists() });
      enqueueSnackbar(`Saved ${response.result.domain}`, { variant: 'success' });
    },
  });
}

export function useDeleteGHAEmailDomainRule() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (domain: string) => await deleteGHAEmailDomainRule(domain),
    onSuccess: (_response, domain) => {
      queryClient.invalidateQueries({ queryKey: ghaEmailDomainRuleQueryKeys.lists() });
      enqueueSnackbar(`Deleted ${domain}`, { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to delete: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useBulkUpsertGHAEmailDomainRules() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (file: File) => await bulkUpsertGHAEmailDomainRules(file),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ghaEmailDomainRuleQueryKeys.lists() });
      enqueueSnackbar(`Uploaded: ${response.result.created} created, ${response.result.updated} updated`, {
        variant: 'success',
      });
    },
  });
}
